/**
 * Secure IndexedDB storage with encryption
 * Provides encrypted storage for sensitive data
 */

import { createStore, get, set, del, keys } from 'idb-keyval';
import { encryptionService, secureStorage } from './encryption';
import { logger } from './logger';

const secureStore = createStore('hakuba-secure-db', 'encrypted-store');

export interface SecureStorageData {
  encrypted: string;
  iv: string;
  hash: string;
  timestamp: number;
  version: string;
}

export class SecureIdbStorage {
  private static instance: SecureIdbStorage;
  private encryptionKey: string | null = null;
  private readonly VERSION = '1.0.0';

  private constructor() {
    // Initialize encryption key asynchronously
    this.initializeKey();
  }

  public static getInstance(): SecureIdbStorage {
    if (!SecureIdbStorage.instance) {
      SecureIdbStorage.instance = new SecureIdbStorage();
    }
    return SecureIdbStorage.instance;
  }

  private async initializeKey(): Promise<void> {
    this.encryptionKey = await this.generateStorageKey();
  }

  /**
   * Generate a unique encryption key for this storage instance
   */
  private async generateStorageKey(): Promise<string> {
    // Create a device-specific fingerprint for encryption
    const deviceFingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width,
      screen.height,
      new Date().getTimezoneOffset(),
      window.location.hostname
    ].join('|');

    return await encryptionService.hash(deviceFingerprint);
  }

  /**
   * Encrypt data before storage
   */
  private async encryptData(data: unknown): Promise<SecureStorageData> {
    // Wait for key initialization if needed
    if (!this.encryptionKey) {
      await this.initializeKey();
    }

    const serialized = JSON.stringify(data);
    const encrypted = await encryptionService.encrypt(
      serialized,
      this.encryptionKey!
    );
    const hash = await encryptionService.hash(serialized);

    return {
      encrypted: encrypted.encrypted,
      iv: encrypted.iv,
      hash,
      timestamp: Date.now(),
      version: this.VERSION
    };
  }

  /**
   * Decrypt data after retrieval
   */
  private async decryptData(secureData: SecureStorageData): Promise<any> {
    // Wait for key initialization if needed
    if (!this.encryptionKey) {
      await this.initializeKey();
    }

    try {
      const decrypted = await encryptionService.decrypt(
        secureData.encrypted,
        this.encryptionKey!
      );

      if (!decrypted.success) {
        throw new Error('Decryption failed');
      }

      // Verify data integrity
      const isValid = await encryptionService.verifyIntegrity(
        decrypted.decrypted,
        secureData.hash
      );

      if (!isValid) {
        throw new Error('Data integrity check failed');
      }

      return JSON.parse(decrypted.decrypted);
    } catch (error) {
      logger.error('Failed to decrypt IndexedDB data', {
        error: error instanceof Error ? error.message : error
      });
      throw new Error('Data decryption failed');
    }
  }

  /**
   * Store encrypted data
   */
  public async setItem<T>(key: string, value: T): Promise<void> {
    try {
      // Check if Web Crypto API is available
      if (!window.crypto || !window.crypto.subtle) {
        logger.warn('Web Crypto API not available, using fallback storage');
        // Use fallback storage instead of throwing error
        const fallbackStorage = FallbackSecureStorage.getInstance();
        await fallbackStorage.setItem(key, value);
        return;
      }

      const secureData = await this.encryptData(value);
      await set(key, secureData, secureStore);
      logger.debug('Data stored securely in IndexedDB', { key });
    } catch (error) {
      logger.error('Failed to store data securely, using fallback', {
        key,
        error: error instanceof Error ? error.message : error
      });

      // Use fallback storage instead of throwing error
      try {
        const fallbackStorage = FallbackSecureStorage.getInstance();
        await fallbackStorage.setItem(key, value);
        logger.debug('Data stored using fallback storage', { key });
      } catch {
        logger.error('Fallback storage also failed', {
          key,
          error: 'Unknown error'
        });
        // Don't throw error, just log it
      }
    }
  }

  /**
   * Retrieve and decrypt data
   */
  public async getItem<T>(key: string): Promise<T | null> {
    try {
      const secureData = await get<SecureStorageData>(key, secureStore);

      if (!secureData) {
        // Try fallback storage
        try {
          const fallbackStorage = FallbackSecureStorage.getInstance();
          return await fallbackStorage.getItem<T>(key);
        } catch {
          logger.debug('No data found in fallback storage', { key });
          return null;
        }
      }

      // Version check for future migrations
      if (secureData.version !== this.VERSION) {
        logger.warn('Data version mismatch, may need migration', {
          key,
          expected: this.VERSION,
          actual: secureData.version
        });
      }

      const decrypted = await this.decryptData(secureData);
      logger.debug('Data retrieved securely from IndexedDB', { key });
      return decrypted;
    } catch (error) {
      logger.error('Failed to retrieve data securely, trying fallback', {
        key,
        error: error instanceof Error ? error.message : error
      });

      // Try fallback storage
      try {
        const fallbackStorage = FallbackSecureStorage.getInstance();
        return await fallbackStorage.getItem<T>(key);
      } catch {
        logger.debug('No data found in fallback storage', { key });
        return null;
      }
    }
  }

  /**
   * Remove encrypted data
   */
  public async removeItem(key: string): Promise<void> {
    try {
      await del(key, secureStore);
      logger.debug('Data removed from secure IndexedDB', { key });
    } catch (error) {
      logger.error('Failed to remove data from secure storage', {
        key,
        error: error instanceof Error ? error.message : error
      });
    }

    // Also try to remove from fallback storage
    try {
      const fallbackStorage = FallbackSecureStorage.getInstance();
      fallbackStorage.removeItem(key);
    } catch {
      logger.debug('Failed to remove from fallback storage', { key });
    }
  }

  /**
   * Get all keys
   */
  public async getKeys(): Promise<string[]> {
    try {
      return await keys(secureStore);
    } catch (error) {
      logger.error('Failed to get keys from secure storage', {
        error: error instanceof Error ? error.message : error
      });
      return [];
    }
  }

  /**
   * Clear all encrypted data
   */
  public async clear(): Promise<void> {
    try {
      const allKeys = await this.getKeys();
      await Promise.all(allKeys.map(key => this.removeItem(key)));
    } catch (error) {
      logger.error('Failed to clear secure storage', {
        error: error instanceof Error ? error.message : error
      });
    }
  }

  /**
   * Check if key exists
   */
  public async hasItem(key: string): Promise<boolean> {
    try {
      const data = await get(key, secureStore);
      return data !== undefined;
    } catch (error) {
      logger.error('Failed to check if item exists', {
        key,
        error: error instanceof Error ? error.message : error
      });
      return false;
    }
  }

  /**
   * Get storage size information
   */
  public async getStorageInfo(): Promise<{
    totalKeys: number;
    totalSize: number;
    keys: string[];
  }> {
    try {
      const allKeys = await this.getKeys();
      let totalSize = 0;

      for (const key of allKeys) {
        const data = await get<SecureStorageData>(key, secureStore);
        if (data) {
          totalSize += JSON.stringify(data).length;
        }
      }

      return {
        totalKeys: allKeys.length,
        totalSize,
        keys: allKeys
      };
    } catch (error) {
      logger.error('Failed to get storage info', {
        error: error instanceof Error ? error.message : error
      });
      return {
        totalKeys: 0,
        totalSize: 0,
        keys: []
      };
    }
  }

  /**
   * Migrate data from old format to new format
   */
  public async migrateData(oldKey: string, newKey: string): Promise<boolean> {
    try {
      const oldData = await get(oldKey, secureStore);
      if (!oldData) {
        return false;
      }

      // Store with new key
      await this.setItem(newKey, oldData);

      // Remove old key
      await del(oldKey, secureStore);

      return true;
    } catch (error) {
      logger.error('Failed to migrate data', {
        oldKey,
        newKey,
        error: error instanceof Error ? error.message : error
      });
      return false;
    }
  }

  /**
   * Backup all data
   */
  public async backup(): Promise<Record<string, any>> {
    try {
      const allKeys = await this.getKeys();
      const backup: Record<string, any> = {};

      for (const key of allKeys) {
        const data = await get<SecureStorageData>(key, secureStore);
        if (data) {
          backup[key] = data;
        }
      }

      return backup;
    } catch (error) {
      logger.error('Failed to create backup', {
        error: error instanceof Error ? error.message : error
      });
      throw new Error('Backup failed');
    }
  }

  /**
   * Restore data from backup
   */
  public async restore(backup: Record<string, any>): Promise<void> {
    try {
      // Clear existing data
      await this.clear();

      // Restore from backup
      for (const [key, secureData] of Object.entries(backup)) {
        await set(key, secureData, secureStore);
      }
    } catch (error) {
      logger.error('Failed to restore from backup', {
        error: error instanceof Error ? error.message : error
      });
      throw new Error('Restore failed');
    }
  }
}

export const secureIdbStorage = SecureIdbStorage.getInstance();

/**
 * Fallback storage that uses secure localStorage when IndexedDB is not available
 */
export class FallbackSecureStorage {
  private static instance: FallbackSecureStorage;

  public static getInstance(): FallbackSecureStorage {
    if (!FallbackSecureStorage.instance) {
      FallbackSecureStorage.instance = new FallbackSecureStorage();
    }
    return FallbackSecureStorage.instance;
  }

  public async setItem<T>(key: string, value: T): Promise<void> {
    try {
      await secureStorage.setItem(key, value);
    } catch (error) {
      logger.error('Fallback storage failed', {
        key,
        error: error instanceof Error ? error.message : error
      });
      throw error;
    }
  }

  public async getItem<T>(key: string): Promise<T | null> {
    try {
      return await secureStorage.getItem<T>(key);
    } catch (error) {
      logger.error('Fallback storage retrieval failed', {
        key,
        error: error instanceof Error ? error.message : error
      });
      return null;
    }
  }

  public removeItem(key: string): void {
    secureStorage.removeItem(key);
  }

  public clear(): void {
    secureStorage.clear();
  }
}

export const fallbackSecureStorage = FallbackSecureStorage.getInstance();
